import { queryClient } from '@/app/queryClient';
import { AuthStoreKeys } from '@/app/constants/storeEnums';

/**
 * Keys in `localStorage` that intentionally survive a session end.
 *
 * - `DeviceId`: stable per-browser identifier sent on every login as
 *   `deviceId`. Regenerating it every logout would break device tracking
 *   and any future trusted-device / per-device refresh-token rotation.
 * - `SidebarIsOpen` / `SidebarUserHasToggled`: pure UX prefs.
 * - `forms-view-mode`: pure UX pref (grid vs. list on the Forms page).
 *
 * The `_enc` suffixed entries exist while the legacy `secureStorage`
 * AES wrapper is still in use; once that wrapper is removed they will
 * simply not be present in `localStorage` and `Set.has` won't match
 * anything — leaving them in this list is a no-op then.
 */
const PRESERVED_LOCAL_STORAGE_KEYS: ReadonlySet<string> = new Set([
  AuthStoreKeys.DeviceId,
  `${AuthStoreKeys.DeviceId}_enc`,
  AuthStoreKeys.SidebarIsOpen,
  `${AuthStoreKeys.SidebarIsOpen}_enc`,
  AuthStoreKeys.SidebarUserHasToggled,
  `${AuthStoreKeys.SidebarUserHasToggled}_enc`,
  'forms-view-mode',
]);

const AUTH_BROADCAST_CHANNEL = 'hh-auth';

export interface ClearSessionOptions {
  /**
   * Whether to broadcast the logout to other tabs of the same origin.
   * Defaults to `true`. Pass `false` from the broadcast receiver itself
   * to avoid an infinite ping-pong between tabs.
   */
  broadcast?: boolean;
}

/**
 * Centralized session teardown. Call from EVERY path that ends a user
 * session: explicit logout (success, server-rejected, network error),
 * token expiry, and cross-tab logout receiver.
 *
 * Clears, in order:
 *   1. React Query in-memory cache (PHI: patient/appointment/etc. queries)
 *   2. All of `sessionStorage` (PHI: open patient tabs, group caches)
 *   3. `localStorage` — selectively, preserving non-credential UX state
 *      (device id, sidebar prefs, forms view mode)
 *   4. PWA `Cache Storage` — defensive; today only static precache lives
 *      there, but this future-proofs against runtime API caching.
 *   5. Broadcasts a logout message so sibling tabs can tear down too.
 *
 * Safe to call from outside the React tree (does not require a hook).
 */
export const clearSession = async ({
  broadcast = true,
}: ClearSessionOptions = {}): Promise<void> => {
  queryClient.clear();

  try {
    sessionStorage.clear();
  } catch (err) {
    console.warn('[clearSession] failed to clear sessionStorage', err);
  }

  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && !PRESERVED_LOCAL_STORAGE_KEYS.has(key)) {
        localStorage.removeItem(key);
      }
    }
  } catch (err) {
    console.warn('[clearSession] failed to clear localStorage', err);
  }

  if (typeof window !== 'undefined' && 'caches' in window) {
    try {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    } catch (err) {
      console.warn('[clearSession] failed to clear Cache Storage', err);
    }
  }

  if (broadcast && typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      channel.postMessage({ type: 'logout' });
      channel.close();
    } catch (err) {
      console.warn('[clearSession] failed to broadcast logout', err);
    }
  }
};

export const AUTH_BROADCAST_CHANNEL_NAME = AUTH_BROADCAST_CHANNEL;
